import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../../../app/context/useApp";
import {
  uploadFile,
  hasProjectKnowledgeXlsxBlockedFile,
  normalizeUploadFiles,
  PROJECT_KNOWLEDGE_XLSX_ERROR,
} from "../../../core/services/fileUpload.service";
import {
  CLIENT_RESOURCE_NAMES,
  uploadClientResources,
} from "../../../core/services/client.service";
import {
  dispatchExpandConversations,
  dispatchMinimizeConversations,
  dispatchMinimizeProjects,
} from "../../sidebarAccordionEvents";
import { dispatchKbUploaded } from "../../kbUploadEvents";
import {
  countUploadFiles,
  createUploadProgressHandler,
  IDLE_SAVE_PROGRESS,
} from "../../../core/utils/uploadProgress";

const getClientId = (client) => client?.clientId ?? client?.Id ?? client?.id ?? null;
const getProjectId = (project) =>
  project?.projectId ?? project?.Id ?? project?.id ?? null;

export default function useHeaderLogic() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    goHome,
    clients = [],
    selectedClient,
    setSelectedClient,
    projects = [],
    archivedProjects = [],
    selectedProject,
    setSelectedProject,
    selectProject,
    createClient,
    updateClient,
    createProject,
    updateProject,
    deleteProject,
    updateProjectIsArchived,
    refreshProjectFiles,
    loading,
    saveProgress,
    isGenerating,
    showAlert,
    createNewChat,
    setMessages,
    selectedSiloIsDefault,
    resetToDefaultHomeSilo,
    clearActiveChatSession,
    messages,
    activeMode,
    setActiveMode,
  } = useApp();

  const [alertOpen, setAlertOpen] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [kbUploading, setKbUploading] = useState(false);
  const [kbUploadProgress, setKbUploadProgress] = useState(IDLE_SAVE_PROGRESS);
  const [kbModalOpen, setKbModalOpen] = useState(false);
  /** When non-null, modal upload includes this projectId; when null, client-level upload only. */
  const [kbModalProjectId, setKbModalProjectId] = useState(null);
  const [clientSwitchPromptOpen, setClientSwitchPromptOpen] = useState(false);
  const [pendingClient, setPendingClient] = useState(null);
  const [projectCreatedAlertOpen, setProjectCreatedAlertOpen] = useState(false);
  const [projectCreatedAlertMessage, setProjectCreatedAlertMessage] = useState("");

  useEffect(() => {
    if (
      selectedClient &&
      !clients.find((c) => String(getClientId(c)) === String(getClientId(selectedClient)))
    ) {
      setSelectedClient(null);
    }
  }, [clients, selectedClient, setSelectedClient]);

  const safeClient = useMemo(
    () =>
      selectedClient &&
      clients.find((c) => String(getClientId(c)) === String(getClientId(selectedClient)))
        ? selectedClient
        : null,
    [clients, selectedClient],
  );

  const handleClientChange = async (client) => {
    if (isGenerating) {
      showAlert(
        "Please Wait",
        "Wait while the response is generating before switching clients.",
      );
      return;
    }

    const previousClient = selectedClient;
    await setSelectedClient(client);
    const hadNoPreviousClient = !getClientId(previousClient);
    const hasSelectedClient = !!getClientId(client);
    const sameClientSelection =
      String(getClientId(previousClient)) === String(getClientId(client));
    const isHomeChatSectionOpen =
      location.pathname === "/" &&
      Array.isArray(messages) &&
      messages.length > 0 &&
      !activeMode;

    if (
      selectedSiloIsDefault &&
      hadNoPreviousClient &&
      hasSelectedClient &&
      !sameClientSelection &&
      isHomeChatSectionOpen
    ) {
      setPendingClient(client);
      setClientSwitchPromptOpen(true);
      navigate("/", { replace: true });
      return;
    }

    if (
      previousClient &&
      String(getClientId(previousClient)) !== String(getClientId(client))
    ) {
      createNewChat(client);
    }

    navigate("/", { replace: true });
  };

  const handleContinueCurrentChat = () => {
    setPendingClient(null);
    setClientSwitchPromptOpen(false);
  };

  const handleStartNewClientChat = () => {
    if (pendingClient) {
      createNewChat(pendingClient);
    } else if (selectedClient) {
      createNewChat(selectedClient);
    }
    setPendingClient(null);
    setClientSwitchPromptOpen(false);
  };

  const activeProjectIdFromRoute = useMemo(() => {
    if (!location.pathname.startsWith("/project/")) return null;
    return location.pathname.split("/project/")[1];
  }, [location.pathname]);

  const activeProjectIdFromSelection = useMemo(() => {
    const id =
      selectedProject?.projectId ??
      selectedProject?.Id ??
      selectedProject?.id ??
      null;
    return id != null ? String(id) : null;
  }, [selectedProject]);

 
  const activeProjectId = activeProjectIdFromSelection ?? activeProjectIdFromRoute;

  
  useEffect(() => {
    if (!activeProjectIdFromRoute) return;

    const project = [...projects, ...archivedProjects].find(
      (p) =>
        String(p?.projectId ?? p?.Id ?? p?.id ?? "") ===
        String(activeProjectIdFromRoute),
    );
    if (!project) return;

    const currentId =
      selectedProject?.projectId ??
      selectedProject?.Id ??
      selectedProject?.id ??
      null;

    if (String(currentId ?? "") === String(activeProjectIdFromRoute)) return;

    setSelectedProject(project);
  }, [
    activeProjectIdFromRoute,
    archivedProjects,
    projects,
    selectedProject,
    setSelectedProject,
  ]);

  const handleProjectClick = (project) => {
    if (isGenerating) return;

    const selectedProjectId = getProjectId(project);
    const isDeselecting = selectedProjectId == null;

    selectProject(project ?? null);

    if (isDeselecting) {
      setActiveMode(null);
      clearActiveChatSession();
      navigate("/", { replace: true });
      return;
    }

    if (location.pathname.startsWith("/project/")) {
      const routeProjectId = location.pathname.split("/project/")[1];
      const shouldCloseWorkroom =
        routeProjectId != null &&
        String(routeProjectId) !== String(selectedProjectId);

      if (shouldCloseWorkroom) {
        setActiveMode(null);
        navigate("/", { replace: true });
      }
    }
  };

  const handleWorkroomClick = (project) => {
    if (isGenerating) return;
    const projectId = getProjectId(project);
    if (!projectId) return;
    selectProject(project ?? null);
    const routeProjectId = location.pathname.startsWith("/project/")
      ? location.pathname.split("/project/")[1]
      : null;
    const isSameWorkroomOpen =
      routeProjectId != null && String(routeProjectId) === String(projectId);

    if (isSameWorkroomOpen) {
      setActiveMode(null);
      createNewChat(safeClient ?? selectedClient);
      dispatchMinimizeProjects();
      dispatchExpandConversations();
      navigate("/", { replace: true });
      return;
    }

    setActiveMode(null);
    dispatchMinimizeConversations();
    navigate(`/project/${projectId}`);
  };

  const shouldShowChatHistory = useMemo(() => {
    if (!safeClient && !selectedSiloIsDefault) return false;

    const hidePaths = [
      "/analyze",
      "/generate",
      "/generate/chatgpt",
      "/generate/gemini",
      "/generate/claude",
      "/audience",
      "/activate",
      "/generate/canva",
    ];

    return (
      !location.pathname.startsWith("/project/") &&
      !hidePaths.includes(location.pathname)
    );
  }, [location.pathname, safeClient, selectedSiloIsDefault]);

  const handleGoHome = async () => {
    if (!isGenerating) {
      // Clear stale session/title so logo → home matches New Chat behavior
      clearActiveChatSession();
      await resetToDefaultHomeSilo();
      await setSelectedClient(null);
      goHome(navigate, setMessages);
    }
  };

  const runKbFileUpload = async (files, projectId) => {
    const clientId = getClientId(safeClient);
    if (!clientId) return false;

    const uploadFiles = normalizeUploadFiles(files);
    const isProjectUpload =
      projectId != null && String(projectId).trim() !== "";
    if (isProjectUpload && hasProjectKnowledgeXlsxBlockedFile(uploadFiles)) {
      showAlert(
        "Invalid file type",
        PROJECT_KNOWLEDGE_XLSX_ERROR,
      );
      return false;
    }

    const fileCount = countUploadFiles(uploadFiles);
    setKbUploading(true);

    const trackProgress = !isProjectUpload;
    if (trackProgress) {
      setKbUploadProgress({
        phase: fileCount > 0 ? "uploading" : "saving",
        percent: fileCount > 0 ? 0 : null,
        current: 0,
        total: fileCount,
      });
    }

    const onUploadProgress = trackProgress
      ? createUploadProgressHandler({
          fileCount,
          onProgress: setKbUploadProgress,
        })
      : undefined;

    try {
      if (isProjectUpload) {
        const res = await uploadFile(uploadFiles, clientId, projectId);
        showAlert(
          "Success",
          res.data?.message || "Knowledge base uploaded successfully.",
          { icon: "success" },
        );
      } else {
        await uploadClientResources(clientId, uploadFiles, {
          resourceName: CLIENT_RESOURCE_NAMES.KNOWLEDGE_BANK,
          onUploadProgress,
        });
        setKbUploadProgress((prev) => ({
          ...prev,
          phase: "processing",
          percent: 100,
          current: fileCount,
          total: fileCount,
        }));
        showAlert("Success", "Knowledge base uploaded successfully.", {
          icon: "success",
        });
      }
      return true;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error.message ||
        "Upload failed.";
      showAlert("Error", message);
      return false;
    } finally {
      setKbUploading(false);
      setKbUploadProgress(IDLE_SAVE_PROGRESS);
    }
  };

  const openClientKbModal = () => {
    setKbModalProjectId(null);
    setKbModalOpen(true);
  };

  const openProjectKbModal = (projectId) => {
    if (projectId == null) return;
    setKbModalProjectId(projectId);
    setKbModalOpen(true);
  };

  const closeKbModal = () => {
    setKbModalOpen(false);
    setKbModalProjectId(null);
  };

  const handleKbModalUpload = async (files) => {
    if (!safeClient) return;
    const projectId =
      kbModalProjectId != null && String(kbModalProjectId).trim() !== ""
        ? kbModalProjectId
        : undefined;
    const ok = await runKbFileUpload(files, projectId);
    if (!ok) return;

    if (projectId != null && refreshProjectFiles) {
      try {
        await refreshProjectFiles(projectId);
      } catch {
        // Alert already shown by refreshProjectFiles.
      }
    }

    dispatchKbUploaded({ projectId: projectId ?? null });
    closeKbModal();
  };

  const handleCreateClient = async (clientData) => {
    try {
      await createClient(clientData);
      const createdName = clientData?.clientName?.trim();
      setProjectCreatedAlertMessage(
        createdName
          ? `Client "${createdName}" was created successfully.`
          : "Your client was created successfully.",
      );
      setProjectCreatedAlertOpen(true);
    } catch (error) {
      throw error;
    }
  };

  const handleUpdateClient = async (clientData) => {
    try {
      await updateClient(clientData);
      const updatedName = clientData?.clientName?.trim();
      setProjectCreatedAlertMessage(
        updatedName
          ? `Client "${updatedName}" was updated successfully.`
          : "Your client was updated successfully.",
      );
      setProjectCreatedAlertOpen(true);
      dispatchKbUploaded();
    } catch (error) {
      throw error;
    }
  };

  const handleCreateProject = async (projectData) => {
    try {
      await createProject(projectData);
      const createdName = projectData?.projectName?.trim();
      setProjectCreatedAlertMessage(
        createdName
          ? `Project "${createdName}" was created successfully.`
          : "Your project was created successfully.",
      );
      setProjectCreatedAlertOpen(true);
    } catch (error) {
      // Error alert is handled in context.
      throw error;
    }
  };

  const handleUpdateProject = async (projectData) => {
    try {
      await updateProject(projectData);
      const updatedName = projectData?.projectName?.trim();
      setProjectCreatedAlertMessage(
        updatedName
          ? `Project "${updatedName}" was updated successfully.`
          : "Your project was updated successfully.",
      );
      setProjectCreatedAlertOpen(true);
      const projectId =
        projectData?.projectId ?? projectData?.Id ?? projectData?.id ?? null;
      if (projectId != null && refreshProjectFiles) {
        try {
          await refreshProjectFiles(projectId);
        } catch {
          // Alert already shown by refreshProjectFiles.
        }
      }
      dispatchKbUploaded({ projectId });
    } catch (error) {
      // Error alert is handled in context.
      throw error;
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!projectId || !deleteProject) return;
    await deleteProject(projectId);

    if (String(activeProjectId ?? "") === String(projectId)) {
      navigate("/", { replace: true });
    }
  };

  return {
    alertOpen,
    setAlertOpen,
    clientModalOpen,
    setClientModalOpen,
    projectModalOpen,
    setProjectModalOpen,
    kbUploading,
    kbUploadProgress,
    kbModalOpen,
    kbUploadForProject: kbModalProjectId != null,
    kbUploadModalKey: String(kbModalProjectId ?? "client"),
    openClientKbModal,
    openProjectKbModal,
    closeKbModal,
    clientSwitchPromptOpen,
    setClientSwitchPromptOpen,
    handleContinueCurrentChat,
    handleStartNewClientChat,
    safeClient,
    handleClientChange,
    handleProjectClick,
    handleWorkroomClick,
    handleGoHome,
    activeProjectId,
    shouldShowChatHistory,
    clients,
    projects,
    archivedProjects,
    isGenerating,
    createClient: handleCreateClient,
    updateClient: handleUpdateClient,
    createProject: handleCreateProject,
    updateProject: handleUpdateProject,
    handleDeleteProject,
    updateProjectIsArchived,
    refreshProjectFiles,
    loading,
    saveProgress,
    handleKbModalUpload,
    projectCreatedAlertOpen,
    setProjectCreatedAlertOpen,
    projectCreatedAlertMessage,
    selectedSiloIsDefault,
  };
}
